// apps/web/src/store/xpSlice.ts
// ─────────────────────────────────────────────────────────────────────────
// XP / LEVEL slice — learning progression.
//
// XP measures "I'm getting smarter / more trained." It is distinct from the
// gem economy (engagement / celebrations). This slice holds the reactive XP
// + level state the top nav, toasts, and Flashcards read from.
//
// Persistence: hydrates from the existing `nis.gamification.xp` key and
// writes back to it, so the legacy SRS XP carries forward untouched
// (Decision 5 — forward-only, no migration of value, no retroactive grants).
// ─────────────────────────────────────────────────────────────────────────

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { levelSnapshot, type LevelSnapshot } from '@nisk/core/rewards';

const XP_KEY = 'nis.gamification.xp';

// ── Storage I/O (kept here so the slice owns its own key) ────────────────
function loadStoredXp(): number {
	try {
		const raw = localStorage.getItem(XP_KEY);
		if (raw) {
			const parsed = JSON.parse(raw);
			if (typeof parsed === 'number') return parsed;
			if (parsed && typeof parsed.total === 'number') return parsed.total;
		}
	} catch {
		/* noop */
	}
	return 0;
}

function persistXp(total: number): void {
	try {
		localStorage.setItem(XP_KEY, JSON.stringify(total));
	} catch {
		/* noop */
	}
}

// ── State ────────────────────────────────────────────────────────────────
export interface XpState {
	totalXp: number;
	level: number;
	levelLabel: string;
	xpIntoLevel: number;
	xpForNextLevel: number;
	progressPct: number;
	lastUpdatedAt?: string;
	/** Set briefly when a level-up happens so UI can celebrate, then cleared. */
	justLeveledUpTo: number | null;
	initialized: boolean;
}

function stateFromTotal(
	totalXp: number,
	extra: Partial<XpState> = {},
): XpState {
	const snap: LevelSnapshot = levelSnapshot(totalXp);
	return {
		totalXp: snap.totalXp,
		level: snap.level,
		levelLabel: snap.levelLabel,
		xpIntoLevel: snap.xpIntoLevel,
		xpForNextLevel: snap.xpForNextLevel,
		progressPct: snap.progressPct,
		justLeveledUpTo: null,
		initialized: true,
		...extra,
	};
}

const initialState: XpState = {
	totalXp: 0,
	level: 1,
	levelLabel: '',
	xpIntoLevel: 0,
	xpForNextLevel: 0,
	progressPct: 0,
	justLeveledUpTo: null,
	initialized: false,
};

// ── Slice ────────────────────────────────────────────────────────────────
const xpSlice = createSlice({
	name: 'xp',
	initialState,
	reducers: {
		/** Hydrate from storage. Call once on boot. */
		initializeXp(state) {
			const hydrated = stateFromTotal(loadStoredXp());
			Object.assign(state, hydrated);
		},

		/**
		 * Add XP and recompute level. Persists immediately. Sets
		 * justLeveledUpTo if the level changed so the dispatcher can fire a
		 * level_up gem reward + the UI can celebrate.
		 */
		addXp(state, action: PayloadAction<number>) {
			const amount = action.payload;
			if (amount <= 0) return;
			const prevLevel = state.level;
			const nextTotal = Math.max(0, state.totalXp + amount);
			persistXp(nextTotal);
			const next = stateFromTotal(nextTotal, {
				lastUpdatedAt: new Date().toISOString(),
			});
			Object.assign(state, next);
			state.justLeveledUpTo = next.level > prevLevel ? next.level : null;
		},

		/** Clear the level-up celebration flag after it's been consumed. */
		clearLevelUp(state) {
			state.justLeveledUpTo = null;
		},

		/**
		 * Snap total XP to a server-authoritative value (multi-device pull
		 * reconciliation). Recomputes level from the new total and persists,
		 * but deliberately does NOT set justLeveledUpTo: XP earned on another
		 * device is folded in silently — no level-up celebration replays here.
		 * Only moves forward; a stale/lower server value is ignored so a lagging
		 * pull can't claw back XP already banked locally.
		 */
		reconcileXp(state, action: PayloadAction<number>) {
			const serverTotal = action.payload;
			if (!Number.isFinite(serverTotal) || serverTotal <= state.totalXp) {
				return;
			}
			persistXp(serverTotal);
			const next = stateFromTotal(serverTotal, {
				lastUpdatedAt: new Date().toISOString(),
			});
			Object.assign(state, next);
			state.justLeveledUpTo = null;
		},
	},
});

export const { initializeXp, addXp, clearLevelUp, reconcileXp } =
	xpSlice.actions;
export default xpSlice.reducer;

// ── Selectors ────────────────────────────────────────────────────────────
export type WithXp = { xp: XpState };

export const selectTotalXp = (s: WithXp) => s.xp.totalXp;
export const selectLevel = (s: WithXp) => s.xp.level;
export const selectLevelLabel = (s: WithXp) => s.xp.levelLabel;
export const selectXpIntoLevel = (s: WithXp) => s.xp.xpIntoLevel;
export const selectXpForNextLevel = (s: WithXp) => s.xp.xpForNextLevel;
export const selectXpProgressPct = (s: WithXp) => s.xp.progressPct;
export const selectJustLeveledUp = (s: WithXp) => s.xp.justLeveledUpTo;
export const selectXpReady = (s: WithXp) => s.xp.initialized;
